import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import api from '@/lib/api';
import {
  Play, X, Check, Square, CheckSquare, AlertTriangle, Save, BadgeCheck,
} from 'lucide-react';

/**
 * Interactive runner for a Universal Procedure.
 * Renders a button that opens a modal where the technician can:
 *  - tick off each step as completed
 *  - record the measured value
 *  - mark each step pass/fail
 *  - add notes
 * On completion the execution is saved and (optionally) promoted to a stored
 * intervention so it enters the knowledge base.
 *
 * Props:
 *   procedure: { id, category, title:{}, steps:[], safety_warnings:{}, required_tools:{} }
 *   context:  { brand, model, machineId, question }
 *   lang:      'it'|'es'|'en'
 *   variant?:  'primary' | 'compact'
 *   onCompleted?: (execution) => void
 */
export default function ProcedureRunner({ procedure, context = {}, lang = 'it', variant = 'primary', onCompleted }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [execution, setExecution] = useState(null);
  const [stepResults, setStepResults] = useState([]);
  const [finalNotes, setFinalNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const title = (procedure.title || {})[lang] || (procedure.title || {}).it || (procedure.title || {}).en || procedure.category;
  const safety = (procedure.safety_warnings || {})[lang] || '';
  const tools = (procedure.required_tools || {})[lang] || '';

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/procedure-executions', {
        universal_procedure_id: procedure.id,
        brand: context.brand || '',
        model: context.model || '',
        machine_id: context.machineId || '',
        context_question: context.question || '',
      });
      setExecution(data);
      setStepResults(data.step_results || []);
      setOpen(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('runner.error_start') || 'Errore avvio procedura');
    } finally { setBusy(false); }
  };

  const updateStep = (idx, patch) => setStepResults((arr) => arr.map((s, i) => i === idx ? { ...s, ...patch } : s));

  const close = (outcome) => async () => {
    if (!execution) { setOpen(false); return; }
    // Guard: if completing without any tick, ask for confirmation
    if (outcome === 'completed' && !stepResults.some((s) => s.completed)) {
      // eslint-disable-next-line no-alert
      const ok = window.confirm(t('runner.no_steps_warning') || 'Nessuno step è stato spuntato come completato. Vuoi salvare comunque?');
      if (!ok) return;
    }
    setBusy(true);
    try {
      const { data } = await api.patch(`/procedure-executions/${execution.id}`, {
        step_results: stepResults,
        outcome,  // 'completed' | 'aborted'
        final_notes: finalNotes,
        promote_to_intervention: outcome === 'completed',
      });
      toast.success(outcome === 'completed' ? (t('runner.saved_completed') || 'Procedura completata e salvata nello storico') : (t('runner.saved_aborted') || 'Procedura annullata'));
      if (onCompleted) onCompleted(data);
      setOpen(false);
      setExecution(null);
      setStepResults([]);
      setFinalNotes('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('runner.error_save') || 'Errore salvataggio');
    } finally { setBusy(false); }
  };

  const saveProgress = async () => {
    if (!execution) return;
    setBusy(true);
    try {
      await api.patch(`/procedure-executions/${execution.id}`, {
        step_results: stepResults,
        final_notes: finalNotes,
      });
      toast.success(t('runner.progress_saved') || 'Progresso salvato');
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('runner.error_generic') || 'Errore');
    } finally { setBusy(false); }
  };

  const Btn = variant === 'compact' ? (
    <button onClick={start} disabled={busy} className="inline-flex items-center gap-1 text-[10px] font-mono uppercase bg-indigo-700 text-white px-2 py-0.5 hover:bg-indigo-800 disabled:opacity-50" data-testid={`runner-start-${procedure.id}`}>
      <Play className="w-2.5 h-2.5" /> {t('runner.start') || 'Esegui'}
    </button>
  ) : (
    <button onClick={start} disabled={busy} className="inline-flex items-center gap-1.5 text-xs font-semibold bg-indigo-700 text-white px-3 py-1.5 hover:bg-indigo-800 active:scale-95 transition-all disabled:opacity-50" data-testid={`runner-start-${procedure.id}`}>
      <Play className="w-3.5 h-3.5" /> {t('runner.start_full') || 'Procedi con questa procedura'}
    </button>
  );

  return (
    <>
      {Btn}
      {open && execution && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 animate-fade-up" data-testid="runner-modal">
          <div className="bg-white border border-gray-200 max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-gray-200 bg-indigo-50 flex items-start gap-3">
              <BadgeCheck className="w-6 h-6 text-indigo-700 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="overline text-indigo-700">{procedure.category}</div>
                <h2 className="font-display font-bold text-lg leading-tight">{title}</h2>
                {context.brand && (
                  <div className="text-xs text-gray-500 mt-1 font-mono">{context.brand}{context.model ? ' · ' + context.model : ''}{context.question ? ' · ' + context.question : ''}</div>
                )}
              </div>
              <button onClick={() => !busy && setOpen(false)} className="btn-ghost shrink-0"><X className="w-4 h-4" /></button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {safety && (
                <div className="border-l-4 border-red-500 bg-red-50 p-3" data-testid="runner-safety">
                  <div className="flex items-center gap-2 text-red-900 font-bold text-sm">
                    <AlertTriangle className="w-4 h-4" /> {t('universal.safety') || 'Avvisi di sicurezza'}
                  </div>
                  <div className="text-sm text-red-900 mt-1 whitespace-pre-wrap">{safety}</div>
                </div>
              )}
              {tools && (
                <div className="text-xs text-gray-700 bg-gray-50 border border-gray-200 p-2">
                  <span className="overline text-gray-500">{t('universal.tools') || 'Strumenti'}:</span> {tools}
                </div>
              )}

              <ol className="space-y-3" data-testid="runner-steps">
                {(procedure.steps || []).map((s, idx) => {
                  const sr = stepResults[idx] || { completed: false, measured_value: '', pass_fail: '', notes: '' };
                  const action = s[lang] || s.it || s.en || s.es || '';
                  return (
                    <li key={s.order || idx} className={`border p-3 ${sr.completed ? 'border-green-300 bg-green-50/40' : 'border-gray-200'}`} data-testid={`runner-step-${s.order || idx + 1}`}>
                      <div className="flex items-start gap-3">
                        <button
                          type="button"
                          onClick={() => updateStep(idx, { completed: !sr.completed })}
                          className={`mt-0.5 w-6 h-6 flex items-center justify-center border ${sr.completed ? 'bg-green-600 text-white border-green-700' : 'bg-white border-gray-400'}`}
                          data-testid={`runner-toggle-${idx}`}
                        >
                          {sr.completed ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-900">
                            {s.order || idx + 1}. {action}
                          </div>
                          {s.expected_value && (
                            <div className="text-xs text-gray-600 font-mono mt-0.5">
                              {t('runner.expected') || 'Atteso'}: <span className="text-green-700">{s.expected_value}</span>
                              {s.tool && <span className="ml-2 text-gray-500">· tool: {s.tool}</span>}
                            </div>
                          )}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                            <input
                              type="text"
                              className="input-base text-xs"
                              placeholder={t('runner.measured') || 'Valore misurato'}
                              value={sr.measured_value}
                              onChange={(e) => updateStep(idx, { measured_value: e.target.value })}
                              data-testid={`runner-value-${idx}`}
                            />
                            <div className="flex gap-1">
                              <button type="button" onClick={() => updateStep(idx, { pass_fail: 'pass' })} className={`flex-1 px-2 py-1 text-[11px] font-mono uppercase border ${sr.pass_fail === 'pass' ? 'bg-green-600 text-white border-green-700' : 'bg-white border-gray-300 hover:bg-green-50'}`}>
                                pass
                              </button>
                              <button type="button" onClick={() => updateStep(idx, { pass_fail: 'fail' })} className={`flex-1 px-2 py-1 text-[11px] font-mono uppercase border ${sr.pass_fail === 'fail' ? 'bg-red-600 text-white border-red-700' : 'bg-white border-gray-300 hover:bg-red-50'}`}>
                                fail
                              </button>
                            </div>
                          </div>
                          <input
                            type="text"
                            className="input-base text-xs mt-2"
                            placeholder={t('runner.notes') || 'Note...'}
                            value={sr.notes}
                            onChange={(e) => updateStep(idx, { notes: e.target.value })}
                          />
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ol>

              <div>
                <label className="overline text-gray-600 block mb-1">{t('runner.final_notes') || 'Note finali'}</label>
                <textarea
                  className="input-base"
                  rows={3}
                  value={finalNotes}
                  onChange={(e) => setFinalNotes(e.target.value)}
                  placeholder={t('runner.final_notes_ph') || 'Conclusioni, anomalie riscontrate, raccomandazioni…'}
                  data-testid="runner-final-notes"
                />
              </div>
            </div>

            <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex flex-wrap items-center justify-end gap-2" data-testid="runner-actions">
              <button onClick={saveProgress} disabled={busy} className="btn-ghost text-xs"><Save className="w-3.5 h-3.5" /> {t('runner.save_progress') || 'Salva progresso'}</button>
              <button onClick={close('aborted')} disabled={busy} className="inline-flex items-center gap-1.5 bg-gray-500 text-white px-3 py-2 text-sm font-semibold hover:bg-gray-600" data-testid="runner-abort">
                <X className="w-4 h-4" /> {t('runner.abort') || 'Annulla'}
              </button>
              <button onClick={close('completed')} disabled={busy} className="inline-flex items-center gap-2 bg-green-700 text-white px-4 py-2 text-sm font-semibold hover:bg-green-800 active:scale-95 transition-all" data-testid="runner-complete">
                <Check className="w-4 h-4" /> {busy ? '…' : (t('runner.complete') || 'Termina e salva')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
